import React from "react";
import { MenuItemPerformance } from "@/redux/services/dashboard/dashboard.type";
import { formatPrice, cn } from "@/lib/utils";
import { getGrowthIndicator } from "@/lib/growthIndicator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRestaurantCurrency } from "@/lib/hooks";

interface TopSellingItemsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: MenuItemPerformance[];
  previousItems?: MenuItemPerformance[];
  type: "top" | "least" | "combo";
}

export const TopSellingItemsModal: React.FC<TopSellingItemsModalProps> = ({
  open,
  onOpenChange,
  items,
  previousItems,
  type,
}) => {
  const currency = useRestaurantCurrency();

  const getTitle = () => {
    switch (type) {
      case "top":
        return "Articles les plus vendus";
      case "combo":
        return "Combos les plus vendus";
      case "least":
        return "Articles les moins vendus";
      default:
        return "";
    }
  };

  const getNumberColor = () => {
    switch (type) {
      case "top":
        return "bg-blue-500";
      case "combo":
        return "bg-purple-500";
      case "least":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getTitle()} - Top 10</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 mt-4">
          {items.slice(0, 10).map((item, index) => {
            const previousItem = previousItems?.find(
              (p) => p.name === item.name
            );
            const isEven = index % 2 === 0;

            return (
              <div
                key={index}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg transition-all",
                  isEven ? "bg-gray-50" : "bg-white",
                  "hover:shadow-sm"
                )}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold",
                      getNumberColor()
                    )}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {item.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {item.orders} commandes •{" "}
                      {formatPrice(item.revenue / 100, currency)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    {item.percentage_of_total.toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-500">des ventes</p>
                  {getGrowthIndicator(item, previousItem, {
                    useThemeColors: false,
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
